import { useState, useEffect } from 'react';
import DOMPurify from 'dompurify';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query';
import { accountApi } from '../api/account';
import { catalogApi } from '../api/catalog';
import { contentApi } from '../api/content';
import type { ContactMessageCreateDto } from '../api/types';
import { Badge, EmptyState, ErrorState, Field, FormErrors, PageLoader, Pagination, Spinner } from '../components/ui';
import { Seo } from '../components/Seo';
import { ProductListPage } from './ProductListPage';
import { formatDate, formatDateTime, money } from '../lib/format';
import {
  financialStatusLabels,
  fulfillmentStatusLabels,
  labelOf,
  orderStatusLabels,
  shipmentStatusLabels,
} from '../lib/labels';

/* --------------------------- Statik sayfa --------------------------- */

export function StaticPage() {
  const { slug = '' } = useParams();

  const page = useQuery({
    queryKey: ['content', 'page', slug],
    queryFn: () => contentApi.page(slug),
    enabled: slug.length > 0,
  });

  if (page.isLoading) return <PageLoader />;
  if (page.isError)
    return (
      <div className="container-page py-12">
        <ErrorState error={page.error} />
      </div>
    );

  const data = page.data!;

  return (
    <article className="container-page max-w-3xl py-10">
      <Seo
        title={data.seoTitle || data.title}
        description={data.seoDescription}
        noIndex={data.noIndex}
      />
      <h1 className="text-2xl font-semibold tracking-tight text-ink-900">{data.title}</h1>
      {data.updatedAt ? (
        <p className="mt-1 text-sm text-ink-400">Son güncelleme: {formatDate(data.updatedAt)}</p>
      ) : null}
      {/* Icerik sunucuda temizlenerek saklanir; istemci tarafinda da DOMPurify ile korunur. */}
      <div className="prose-content mt-6" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(data.content) }} />
    </article>
  );
}

/* ------------------------------- Blog ------------------------------- */

export function BlogListPage() {
  const [params, setParams] = useSearchParams();
  const page = Number(params.get('page') ?? 1);
  const category = params.get('kategori') ?? undefined;
  const tag = params.get('etiket') ?? undefined;

  const posts = useQuery({
    queryKey: ['content', 'blog', page, category, tag],
    queryFn: () => contentApi.blogPosts({ page, pageSize: 9 }, category, tag),
    placeholderData: keepPreviousData,
  });

  if (posts.isLoading) return <PageLoader />;
  if (posts.isError) return (
    <div className="container-page py-12">
      <ErrorState error={posts.error} onRetry={() => void posts.refetch()} />
    </div>
  );

  const data = posts.data!;

  return (
    <div className="container-page py-10">
      <Seo
        title="Blog & Rehberler"
        description="Ürün rehberleri, teknoloji ve moda trendleri, alışveriş ipuçları ve mağaza haberleri."
      />
      <h1 className="mb-2 text-2xl font-semibold tracking-tight text-ink-900">Blog</h1>
      <p className="mb-8 text-sm text-ink-600">Ürün rehberleri, kampanyalar ve mağaza haberleri.</p>

      {(category || tag) ? (
        <div className="mb-6 flex items-center gap-2 text-sm">
          <span className="text-ink-500">Filtre:</span>
          <span className="badge bg-ink-100 text-ink-700">{category ?? tag}</span>
          <button
            type="button"
            className="link"
            onClick={() => setParams(new URLSearchParams())}
          >
            Temizle
          </button>
        </div>
      ) : null}

      {data.items.length === 0 ? (
        <EmptyState title="Henüz yazı yok" description="Yakında burada içerik paylaşacağız." />
      ) : (
        <>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {data.items.map((post) => (
              <Link
                key={post.id}
                to={`/blog/${post.slug}`}
                className="flex flex-col overflow-hidden rounded-xl border border-ink-200 transition hover:shadow-card"
              >
                <div className="aspect-[16/9] bg-ink-100">
                  {post.coverImageUrl ? (
                    <img src={post.coverImageUrl} alt="" className="h-full w-full object-cover" />
                  ) : null}
                </div>
                <div className="flex flex-1 flex-col p-5">
                  <div className="flex items-center gap-2 text-xs text-ink-400">
                    <span>{formatDate(post.publishedAt)}</span>
                    {post.readingTimeMinutes > 0 ? <span>· {post.readingTimeMinutes} dk okuma</span> : null}
                  </div>
                  <h2 className="mt-2 line-clamp-2 text-base font-semibold text-ink-900">{post.title}</h2>
                  {post.summary ? (
                    <p className="mt-2 line-clamp-3 text-sm text-ink-600">{post.summary}</p>
                  ) : null}
                  {post.categoryName ? (
                    <span className="badge mt-auto self-start bg-ink-100 pt-3 text-ink-600">
                      {post.categoryName}
                    </span>
                  ) : null}
                </div>
              </Link>
            ))}
          </div>

          <Pagination
            page={data.page}
            totalPages={data.totalPages}
            totalCount={data.totalCount}
            onChange={(next) => {
              const updated = new URLSearchParams(params);
              updated.set('page', String(next));
              setParams(updated);
            }}
          />
        </>
      )}
    </div>
  );
}

export function BlogDetailPage() {
  const { slug = '' } = useParams();

  const post = useQuery({
    queryKey: ['content', 'blog', slug],
    queryFn: () => contentApi.blogPost(slug),
    enabled: slug.length > 0,
  });

  if (post.isLoading) return <PageLoader />;
  if (post.isError)
    return (
      <div className="container-page py-12">
        <ErrorState error={post.error} />
      </div>
    );

  const data = post.data!;

  return (
    <article className="container-page max-w-3xl py-10">
      <Seo
        title={data.title}
        description={data.summary || (data.content ? data.content.slice(0, 160).replace(/<[^>]*>/g, '') : null)}
        image={data.coverImageUrl}
        type="article"
      />
      <Link to="/blog" className="text-sm text-ink-500 hover:text-ink-800">
        ← Tüm yazılar
      </Link>

      <h1 className="mt-4 text-3xl font-semibold tracking-tight text-ink-900">{data.title}</h1>

      <div className="mt-3 flex flex-wrap items-center gap-3 text-sm text-ink-500">
        <span>{formatDate(data.publishedAt)}</span>
        {data.authorName ? <span>· {data.authorName}</span> : null}
        {data.readingTimeMinutes > 0 ? <span>· {data.readingTimeMinutes} dk okuma</span> : null}
        {data.categoryName ? <span className="badge bg-ink-100 text-ink-600">{data.categoryName}</span> : null}
      </div>

      {data.coverImageUrl ? (
        <img
          src={data.coverImageUrl}
          alt=""
          className="mt-6 aspect-[16/9] w-full rounded-xl object-cover"
        />
      ) : null}

      {data.content ? (
        <div className="prose-content mt-8" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(data.content) }} />
      ) : null}

      {data.featuredProductIds.length > 0 ? <FeaturedProducts ids={data.featuredProductIds} /> : null}
    </article>
  );
}

function FeaturedProducts({ ids }: { ids: number[] }) {
  const products = useQuery({
    queryKey: ['products', 'by-ids', ids],
    queryFn: async () => {
      const results = await Promise.all(
        ids.slice(0, 4).map((id) => catalogApi.relatedProducts(id, 1).catch(() => [])),
      );
      return results.flat();
    },
    enabled: ids.length > 0,
  });

  if (!products.data || products.data.length === 0) return null;

  return (
    <section className="mt-10 border-t border-ink-200 pt-8">
      <h2 className="mb-4 text-lg font-semibold text-ink-900">Yazıda geçen ürünler</h2>
      <ul className="grid gap-4 sm:grid-cols-2">
        {products.data.map((product) => (
          <li key={product.id}>
            <Link
              to={`/urun/${product.slug}`}
              className="flex items-center gap-3 rounded-xl border border-ink-200 p-3 transition hover:shadow-card"
            >
              <div className="h-16 w-16 shrink-0 overflow-hidden rounded-lg bg-ink-50">
                {product.primaryImageUrl ? (
                  <img src={product.primaryImageUrl} alt="" className="h-full w-full object-cover" />
                ) : null}
              </div>
              <div className="min-w-0">
                <p className="truncate text-sm font-medium text-ink-900">{product.title}</p>
                <p className="text-sm text-ink-600">{money(product.minPrice, product.currencyCode)}</p>
              </div>
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}

/* -------------------------------- SSS -------------------------------- */

export function FaqPage() {
  const [category, setCategory] = useState<string | undefined>(undefined);
  const [openId, setOpenId] = useState<number | null>(null);

  const faq = useQuery({
    queryKey: ['content', 'faq', category],
    queryFn: () => contentApi.faq(category),
  });

  const categories = Array.from(
    new Set((faq.data ?? []).map((item) => item.category).filter((value): value is string => !!value)),
  );

  if (faq.isLoading) return <PageLoader />;

  return (
    <div className="container-page max-w-3xl py-10">
      <Seo
        title="Sıkça Sorulan Sorular"
        description="Sipariş, ödeme, kargo, teslimat ve iade süreçleri hakkında merak edilen tüm sorular ve yanıtları."
      />
      <h1 className="mb-2 text-2xl font-semibold tracking-tight text-ink-900">
        Sıkça sorulan sorular
      </h1>
      <p className="mb-8 text-sm text-ink-600">
        Aradığınızı bulamazsanız{' '}
        <Link to="/iletisim" className="link">
          bize yazın
        </Link>
        .
      </p>

      {categories.length > 0 ? (
        <div className="mb-6 flex flex-wrap gap-2">
          <button
            type="button"
            className={category === undefined ? 'btn btn-sm bg-ink-900 text-white' : 'btn-secondary btn-sm'}
            onClick={() => setCategory(undefined)}
          >
            Tümü
          </button>
          {categories.map((name) => (
            <button
              key={name}
              type="button"
              className={category === name ? 'btn btn-sm bg-ink-900 text-white' : 'btn-secondary btn-sm'}
              onClick={() => setCategory(name)}
            >
              {name}
            </button>
          ))}
        </div>
      ) : null}

      {faq.data && faq.data.length > 0 ? (
        <ul className="divide-y divide-ink-100 rounded-xl border border-ink-200">
          {faq.data.map((item) => (
            <li key={item.id}>
              <button
                type="button"
                className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left"
                aria-expanded={openId === item.id}
                onClick={() => setOpenId((current) => (current === item.id ? null : item.id))}
              >
                <span className="text-sm font-medium text-ink-900">{item.question}</span>
                <svg
                  viewBox="0 0 20 20"
                  className={`h-4 w-4 shrink-0 text-ink-400 transition ${openId === item.id ? 'rotate-180' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.75"
                >
                  <path d="M5 7.5l5 5 5-5" strokeLinecap="round" />
                </svg>
              </button>
              {openId === item.id ? (
                <div
                  className="prose-content px-5 pb-5 text-sm"
                  dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(item.answer) }}
                />
              ) : null}
            </li>
          ))}
        </ul>
      ) : (
        <EmptyState title="Soru bulunamadı" />
      )}
    </div>
  );
}

/* ----------------------------- Iletisim ----------------------------- */

export function ContactPage() {
  const [form, setForm] = useState<ContactMessageCreateDto>({
    fullName: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
    relatedOrderNumber: '',
  });

  const settings = useQuery({
    queryKey: ['content', 'settings'],
    queryFn: () => contentApi.publicSettings(),
    staleTime: 10 * 60_000,
  });

  const send = useMutation({
    mutationFn: () => contentApi.submitContact(form),
  });

  const storeEmail = settings.data?.['store.email'] || settings.data?.['store.supportEmail'] || '';
  const storePhone = settings.data?.['store.phone'] || settings.data?.['store.supportPhone'] || '';
  const storeAddress = settings.data?.['store.address'] || settings.data?.['store.companyAddress'] || '';
  const whatsapp = settings.data?.['social.whatsapp'] || '';

  return (
    <div className="container-page py-10">
      <Seo
        title="İletişim & Müşteri Hizmetleri"
        description="Sorularınız, önerileriniz veya destek talepleriniz için bize ulaşın. Müşteri temsilcilerimiz en kısa sürede dönüş yapacaktır."
      />
      <div className="grid gap-10 lg:grid-cols-2">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-ink-900">İletişim</h1>
          <p className="mt-2 text-sm text-ink-600">
            Sorularınız için formu doldurun veya aşağıdaki iletişim kanallarımızdan bize doğrudan ulaşın; en kısa sürede dönüş yapalım.
          </p>

          <dl className="mt-8 space-y-5 text-sm">
            {storePhone ? (
              <div className="flex items-start gap-3.5 rounded-xl border border-ink-200 bg-white p-4 shadow-xs">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-brand-50 text-brand-700">
                  <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" />
                  </svg>
                </div>
                <div>
                  <dt className="font-semibold text-ink-900">Müşteri Hizmetleri Telefonu</dt>
                  <dd className="mt-0.5 text-ink-600">
                    <a href={`tel:${storePhone.replace(/\s+/g, '')}`} className="hover:text-brand-700 transition-colors font-medium">
                      {storePhone}
                    </a>
                  </dd>
                </div>
              </div>
            ) : null}

            {storeEmail ? (
              <div className="flex items-start gap-3.5 rounded-xl border border-ink-200 bg-white p-4 shadow-xs">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-brand-50 text-brand-700">
                  <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
                    <rect width="20" height="16" x="2" y="4" rx="2" />
                    <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
                  </svg>
                </div>
                <div>
                  <dt className="font-semibold text-ink-900">Destek ve İletişim E-Postası</dt>
                  <dd className="mt-0.5 text-ink-600">
                    <a href={`mailto:${storeEmail}`} className="hover:text-brand-700 transition-colors font-medium">
                      {storeEmail}
                    </a>
                  </dd>
                </div>
              </div>
            ) : null}

            {storeAddress ? (
              <div className="flex items-start gap-3.5 rounded-xl border border-ink-200 bg-white p-4 shadow-xs">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-brand-50 text-brand-700">
                  <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z" />
                    <circle cx="12" cy="10" r="3" />
                  </svg>
                </div>
                <div>
                  <dt className="font-semibold text-ink-900">Mağaza / Şirket Adresi</dt>
                  <dd className="mt-0.5 text-ink-600">{storeAddress}</dd>
                </div>
              </div>
            ) : null}

            {whatsapp ? (
              <div className="flex items-start gap-3.5 rounded-xl border border-ink-200 bg-white p-4 shadow-xs">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-emerald-50 text-emerald-700">
                  <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
                  </svg>
                </div>
                <div>
                  <dt className="font-semibold text-ink-900">WhatsApp Destek Hattı</dt>
                  <dd className="mt-0.5 text-ink-600">
                    <a
                      href={`https://wa.me/${whatsapp.replace(/[^0-9]/g, '')}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-emerald-700 hover:underline font-medium"
                    >
                      WhatsApp üzerinden mesaj gönderin →
                    </a>
                  </dd>
                </div>
              </div>
            ) : null}
          </dl>
        </div>

        <div className="card p-6">
          {send.isSuccess ? (
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
              {send.data ?? 'Mesajınız alındı. En kısa sürede dönüş yapacağız.'}
            </div>
          ) : (
            <form
              className="space-y-4"
              onSubmit={(event) => {
                event.preventDefault();
                send.mutate();
              }}
            >
              <FormErrors error={send.error} />

              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Ad soyad" htmlFor="contact-name" required>
                  <input
                    id="contact-name"
                    required
                    className="field"
                    value={form.fullName}
                    onChange={(event) => setForm((c) => ({ ...c, fullName: event.target.value }))}
                  />
                </Field>
                <Field label="E-posta" htmlFor="contact-email" required>
                  <input
                    id="contact-email"
                    type="email"
                    required
                    className="field"
                    value={form.email}
                    onChange={(event) => setForm((c) => ({ ...c, email: event.target.value }))}
                  />
                </Field>
                <Field label="Telefon" htmlFor="contact-phone">
                  <input
                    id="contact-phone"
                    type="tel"
                    className="field"
                    value={form.phone ?? ''}
                    onChange={(event) => setForm((c) => ({ ...c, phone: event.target.value }))}
                  />
                </Field>
                <Field label="Sipariş numarası" htmlFor="contact-order">
                  <input
                    id="contact-order"
                    className="field"
                    placeholder="SP-1001"
                    value={form.relatedOrderNumber ?? ''}
                    onChange={(event) =>
                      setForm((c) => ({ ...c, relatedOrderNumber: event.target.value }))
                    }
                  />
                </Field>
              </div>

              <Field label="Konu" htmlFor="contact-subject" required>
                <input
                  id="contact-subject"
                  required
                  className="field"
                  value={form.subject ?? ''}
                  onChange={(event) => setForm((c) => ({ ...c, subject: event.target.value }))}
                />
              </Field>

              <Field label="Mesajınız" htmlFor="contact-message" required>
                <textarea
                  id="contact-message"
                  required
                  className="field min-h-32"
                  maxLength={4000}
                  value={form.message}
                  onChange={(event) => setForm((c) => ({ ...c, message: event.target.value }))}
                />
              </Field>

              <button type="submit" className="btn-primary w-full" disabled={send.isPending}>
                {send.isPending ? <Spinner className="h-4 w-4" /> : null}
                Gönder
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

/* --------------------------- Bulten islemi --------------------------- */

export function NewsletterActionPage() {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const isUnsubPath =
    typeof window !== 'undefined' &&
    (window.location.pathname.includes('iptal') || window.location.pathname.includes('cikis'));
  const action = params.get('islem') ?? (isUnsubPath ? 'cikis' : 'confirm');
  const [reason, setReason] = useState('');
  const [copied, setCopied] = useState(false);

  const confirm = useMutation({ mutationFn: () => contentApi.confirmNewsletter(token) });
  const unsubscribe = useMutation({
    mutationFn: () => contentApi.unsubscribeNewsletter(token, reason || undefined),
  });

  // E-postadaki onay linkine tıklandığında otomatik olarak onaylansın
  useEffect(() => {
    if (token && action !== 'cikis') {
      confirm.mutate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, action]);

  const active = action === 'cikis' ? unsubscribe : confirm;

  const handleCopy = (code: string) => {
    if (!code) return;
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 3000);
  };

  return (
    <div className="container-page max-w-lg py-16">
      <Seo
        title={action === 'cikis' ? 'Bülten Aboneliğinden Çık' : 'Bülten Aboneliği Onayı'}
        noIndex
      />
      <div className="card p-6 sm:p-8 text-center">
        <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-50 text-3xl shadow-sm">
          {action === 'cikis' ? '✉️' : '🎉'}
        </div>
        <h1 className="text-xl sm:text-2xl font-bold text-ink-900">
          {action === 'cikis' ? 'Bülten Aboneliğinden Çık' : 'E-Bülten Aboneliği Onayı'}
        </h1>

        {active.isPending ? (
          <div className="mt-8 flex flex-col items-center gap-3 py-6 text-sm text-ink-600">
            <Spinner className="h-6 w-6 text-brand-600" />
            <span className="font-medium">İşleminiz gerçekleştiriliyor, kuponunuz hazırlanıyor…</span>
          </div>
        ) : active.isSuccess ? (
          action === 'cikis' ? (
            <div className="mt-6 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3.5 text-sm font-medium text-emerald-800">
              {unsubscribe.data ?? 'Bülten aboneliğiniz sonlandırıldı.'}
            </div>
          ) : (
            <div className="mt-6 space-y-5">
              <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm font-medium text-emerald-800">
                ✨ {confirm.data?.message ?? 'Bülten aboneliğiniz başarıyla onaylandı!'}
              </div>

              {confirm.data?.couponCode ? (
                <div className="relative overflow-hidden rounded-2xl border-2 border-dashed border-emerald-400 bg-gradient-to-br from-emerald-50/70 via-white to-brand-50/40 p-5 sm:p-6 shadow-sm text-left">
                  {/* Kupon Bilet Çentikleri */}
                  <div className="absolute -left-3 top-1/2 -mt-3 h-6 w-6 rounded-full bg-surface-card border border-emerald-300" />
                  <div className="absolute -right-3 top-1/2 -mt-3 h-6 w-6 rounded-full bg-surface-card border border-emerald-300" />

                  <div className="flex items-center justify-between gap-2 border-b border-dashed border-emerald-200 pb-3">
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-100 px-3 py-1 text-xs font-semibold text-emerald-800">
                      🎟️ Hoş Geldin Kuponu
                    </span>
                    <span className="text-xl sm:text-2xl font-black text-emerald-700">
                      {money(confirm.data.discountAmount)} İndirim
                    </span>
                  </div>

                  <div className="mt-4">
                    <p className="text-xs text-ink-500 font-medium">İndirim Kuponu Kodunuz</p>
                    <div className="mt-1 flex items-center justify-between gap-2 rounded-xl bg-surface-card border border-ink-200 p-2 pl-3">
                      <span className="font-mono text-lg font-bold tracking-wider text-ink-900 select-all">
                        {confirm.data.couponCode}
                      </span>
                      <button
                        type="button"
                        onClick={() => handleCopy(confirm.data!.couponCode)}
                        className={`btn btn-sm shrink-0 transition ${
                          copied
                            ? 'bg-emerald-600 text-white hover:bg-emerald-700'
                            : 'btn-secondary text-xs'
                        }`}
                      >
                        {copied ? '✓ Kopyalandı' : 'Kodu Kopyala'}
                      </button>
                    </div>
                  </div>

                  <div className="mt-3.5 text-xs text-ink-600">
                    {confirm.data.minimumSubtotal && confirm.data.minimumSubtotal > 0 ? (
                      <p className="flex items-center gap-1.5 font-medium text-amber-900">
                        <span>ℹ️</span> Bu kupon <strong>{money(confirm.data.minimumSubtotal)}</strong> ve üzeri siparişlerde geçerlidir.
                      </p>
                    ) : (
                      <p className="flex items-center gap-1.5 font-medium text-emerald-700">
                        <span>✓</span> Bu kupon tüm alışverişlerinizde alt limitsiz geçerlidir.
                      </p>
                    )}
                  </div>

                  {confirm.data.isAssignedToAccount ? (
                    <div className="mt-3.5 rounded-xl bg-brand-50/90 p-3 text-xs text-brand-900 flex items-start gap-2 border border-brand-200/50">
                      <span className="text-base leading-none">✨</span>
                      <span>
                        Kuponunuz doğrudan hesabınıza tanımlandı! Sepetinizde <strong>"Kayıtlı Kuponlarımdan Seç"</strong> butonuna tıklayarak tek tıkla uygulayabilirsiniz.
                      </span>
                    </div>
                  ) : (
                    <div className="mt-3.5 rounded-xl bg-ink-50 p-3 text-xs text-ink-600 flex items-start gap-2 border border-ink-200/60">
                      <span className="text-base leading-none">💡</span>
                      <span>
                        Alışverişinizi tamamlarken sepette indirim kuponu alanına bu kodu yapıştırarak hemen indirimden yararlanabilirsiniz.
                      </span>
                    </div>
                  )}
                </div>
              ) : null}

              <div className="flex flex-col sm:flex-row gap-2.5 pt-2">
                <Link to="/sepet" className="btn-primary flex-1 justify-center py-2.5 text-sm">
                  Sepetime Git
                </Link>
                <Link to="/kategoriler" className="btn-secondary flex-1 justify-center py-2.5 text-sm">
                  Alışverişe Başla
                </Link>
              </div>
            </div>
          )
        ) : (
          <div className="mt-6 space-y-4 text-left">
            <FormErrors error={active.error} />

            {action === 'cikis' ? (
              <Field label="Ayrılma nedeniniz (isteğe bağlı)" htmlFor="unsub-reason">
                <textarea
                  id="unsub-reason"
                  className="field min-h-20"
                  value={reason}
                  onChange={(event) => setReason(event.target.value)}
                />
              </Field>
            ) : null}

            <button
              type="button"
              className="btn-primary w-full justify-center"
              disabled={!token || active.isPending}
              onClick={() => active.mutate()}
            >
              {active.isPending ? <Spinner className="h-4 w-4" /> : null}
              {action === 'cikis' ? 'Abonelikten Çık' : 'Kaydı Onayla'}
            </button>

            {!token ? (
              <p className="text-center text-sm text-red-600">
                Bağlantı geçersiz: doğrulama anahtarı bulunamadı.
              </p>
            ) : null}
          </div>
        )}

        <Link to="/" className="btn-ghost mt-5 w-full justify-center text-xs text-ink-500">
          Ana sayfaya dön
        </Link>
      </div>
    </div>
  );
}

/* -------------------------- Siparis takibi -------------------------- */

export function OrderTrackingPage() {
  const { publicId } = useParams<{ publicId?: string }>();
  const [searchParams] = useSearchParams();

  const queryOrderNo = searchParams.get('no') || '';
  const queryEmail = searchParams.get('email') || '';

  const [orderNumber, setOrderNumber] = useState(queryOrderNo);
  const [email, setEmail] = useState(queryEmail);

  const publicIdQuery = useQuery({
    queryKey: ['order-tracking', publicId],
    queryFn: () => accountApi.trackOrderByPublicId(publicId!),
    enabled: !!publicId && publicId.length > 0,
    retry: false,
  });

  const track = useMutation({
    mutationFn: (args?: { num?: string; mail?: string }) =>
      accountApi.trackOrder(
        (args?.num ?? orderNumber).trim(),
        (args?.mail ?? email).trim()
      ),
  });

  useEffect(() => {
    if (queryOrderNo) setOrderNumber(queryOrderNo);
    if (queryEmail) setEmail(queryEmail);

    if (queryOrderNo && queryEmail) {
      track.mutate({ num: queryOrderNo, mail: queryEmail });
    }
  }, [queryOrderNo, queryEmail]);

  useEffect(() => {
    if (publicIdQuery.data) {
      setOrderNumber(publicIdQuery.data.orderNumber);
    }
  }, [publicIdQuery.data]);

  const trackedData = publicIdQuery.data ?? track.data;
  const isPending = publicIdQuery.isLoading || track.isPending;
  const error = publicIdQuery.error || track.error;

  return (
    <div className="container-page max-w-3xl py-10">
      <h1 className="text-2xl font-semibold tracking-tight text-ink-900">Sipariş takibi</h1>
      <p className="mt-2 text-sm text-ink-600">
        Sipariş numaranız ve sipariş verirken kullandığınız e-posta ile siparişinizi sorgulayabilirsiniz.
      </p>

      <form
        className="card mt-6 grid gap-4 p-5 sm:grid-cols-[1fr_1fr_auto] sm:items-end"
        onSubmit={(event) => {
          event.preventDefault();
          track.mutate();
        }}
      >
        <Field label="Sipariş numarası" htmlFor="track-order" required>
          <input
            id="track-order"
            required
            className="field"
            placeholder="SP-1001"
            value={orderNumber}
            onChange={(event) => setOrderNumber(event.target.value)}
          />
        </Field>
        <Field label="E-posta" htmlFor="track-email" required>
          <input
            id="track-email"
            type="email"
            required
            className="field"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />
        </Field>
        <button type="submit" className="btn-primary" disabled={isPending}>
          {isPending ? <Spinner className="h-4 w-4" /> : null}
          Sorgula
        </button>
      </form>

      <div className="mt-6">
        <FormErrors error={error} />
      </div>

      {trackedData ? (
        <div className="mt-6 space-y-6">
          <section className="card p-5">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <h2 className="text-lg font-semibold text-ink-900">{trackedData.orderNumber}</h2>
                <p className="text-sm text-ink-500">{formatDateTime(trackedData.createdAt)}</p>
              </div>
              <span className="text-lg font-semibold text-ink-900">
                {money(trackedData.grandTotal, trackedData.currencyCode)}
              </span>
            </div>

            <div className="mt-3 flex flex-wrap gap-2">
              <Badge label={labelOf(orderStatusLabels, trackedData.status)} />
              <Badge label={labelOf(financialStatusLabels, trackedData.financialStatus)} />
              <Badge label={labelOf(fulfillmentStatusLabels, trackedData.fulfillmentStatus)} />
            </div>

            {trackedData.maskedShippingAddress ? (
              <p className="mt-4 text-sm text-ink-600">
                Teslimat: {trackedData.maskedShippingAddress}
              </p>
            ) : null}

            {trackedData.estimatedDeliveryFrom ? (
              <p className="mt-1 text-sm text-ink-600">
                Tahmini teslim: {formatDate(trackedData.estimatedDeliveryFrom)}
                {trackedData.estimatedDeliveryTo
                  ? ` – ${formatDate(trackedData.estimatedDeliveryTo)}`
                  : ''}
              </p>
            ) : null}
          </section>

          <section className="card overflow-hidden">
            <h3 className="border-b border-ink-200 px-5 py-3.5 text-sm font-semibold text-ink-900">
              Ürünler
            </h3>
            <ul className="divide-y divide-ink-100">
              {trackedData.lines.map((line) => (
                <li key={line.id} className="flex items-center gap-3 p-4">
                  <div className="h-12 w-12 shrink-0 overflow-hidden rounded bg-ink-50">
                    {line.imageUrl ? (
                      <img src={line.imageUrl} alt="" className="h-full w-full object-cover" />
                    ) : null}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm text-ink-900">{line.productTitle}</p>
                    {line.variantTitle ? (
                      <p className="truncate text-xs text-ink-500">{line.variantTitle}</p>
                    ) : null}
                  </div>
                  <span className="text-sm text-ink-600">{line.quantity} adet</span>
                </li>
              ))}
            </ul>
          </section>

          {trackedData.fulfillments.length > 0 ? (
            <section className="card p-5">
              <h3 className="mb-4 text-sm font-semibold text-ink-900">Kargo hareketleri</h3>
              {trackedData.fulfillments.map((fulfillment) => (
                <div key={fulfillment.id} className="mb-4 last:mb-0">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <p className="text-sm text-ink-700">
                      {fulfillment.carrierName ?? 'Kargo'}
                      {fulfillment.trackingNumber ? ` · ${fulfillment.trackingNumber}` : ''}
                    </p>
                    <Badge label={labelOf(shipmentStatusLabels, fulfillment.trackingStatus)} />
                  </div>
                  {fulfillment.trackingEvents.length > 0 ? (
                    <ol className="mt-3 space-y-2 border-l border-ink-200 pl-4">
                      {fulfillment.trackingEvents.map((event) => (
                        <li key={`${event.occurredAt}-${event.status}`} className="relative text-sm">
                          <span className="absolute -left-[21px] top-1.5 h-2 w-2 rounded-full bg-ink-300" />
                          <p className="text-ink-700">{event.description ?? event.status}</p>
                          <p className="text-xs text-ink-400">
                            {formatDateTime(event.occurredAt)}
                            {event.location ? ` · ${event.location}` : ''}
                          </p>
                        </li>
                      ))}
                    </ol>
                  ) : null}
                </div>
              ))}
            </section>
          ) : null}

          {trackedData.timeline.length > 0 ? (
            <section className="card p-5">
              <h3 className="mb-4 text-sm font-semibold text-ink-900">Sipariş geçmişi</h3>
              <ol className="space-y-3 border-l border-ink-200 pl-4">
                {trackedData.timeline.map((event) => (
                  <li key={event.id} className="relative text-sm">
                    <span className="absolute -left-[21px] top-1.5 h-2 w-2 rounded-full bg-brand-500" />
                    <p className="text-ink-700">{event.message}</p>
                    <p className="text-xs text-ink-400">{formatDateTime(event.createdAt)}</p>
                  </li>
                ))}
              </ol>
            </section>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

/* -------------------- Kategori / marka / koleksiyon -------------------- */

export function CategoryPage() {
  const { slug = '' } = useParams();

  const category = useQuery({
    queryKey: ['category', slug],
    queryFn: () => catalogApi.categoryBySlug(slug),
    enabled: slug.length > 0,
  });

  const categoriesTree = useQuery({
    queryKey: ['catalog', 'category-tree'],
    queryFn: () => catalogApi.categoryTree(),
    staleTime: 5 * 60_000,
  });

  const breadcrumbs = useQuery({
    queryKey: ['category', category.data?.id, 'breadcrumbs'],
    queryFn: () => catalogApi.categoryBreadcrumbs(category.data!.id),
    enabled: category.data?.id !== undefined,
  });

  // Kategori ağacından mevcut kategorinin alt kategorilerini bul
  const subCategories = (() => {
    if (!categoriesTree.data || !category.data) return [];
    const findNode = (nodes: typeof categoriesTree.data): (typeof categoriesTree.data)[number] | null => {
      for (const node of nodes) {
        if (node.id === category.data!.id || node.slug === slug) return node;
        if (node.children?.length) {
          const found = findNode(node.children);
          if (found) return found;
        }
      }
      return null;
    };
    return findNode(categoriesTree.data)?.children ?? [];
  })();

  if (category.isLoading) return <PageLoader />;
  if (category.isError)
    return (
      <div className="container-page py-12">
        <ErrorState error={category.error} />
      </div>
    );

  return (
    <>
      <Seo
        title={category.data?.seoTitle || (category.data ? `${category.data.name} Modelleri ve Fiyatları` : null)}
        description={
          category.data?.seoDescription ||
          category.data?.description ||
          (category.data ? `${category.data.name} kategorisindeki en yeni ve kaliteli ürünleri avantajlı fiyatlarla keşfedin.` : null)
        }
        keywords={category.data?.seoKeywords}
        image={category.data?.imageUrl || category.data?.bannerUrl}
        breadcrumbs={[
          { name: 'Ana sayfa', url: '/' },
          ...(breadcrumbs.data ?? []).map((crumb) => ({
            name: crumb.name,
            url: `/kategori/${crumb.slug}`,
          })),
        ]}
      />
      {breadcrumbs.data && breadcrumbs.data.length > 0 ? (
        <nav aria-label="Kırılım" className="container-page pt-6 text-sm text-ink-500">
          <Link to="/" className="hover:text-ink-800">
            Ana sayfa
          </Link>
          {breadcrumbs.data.map((crumb) => (
            <span key={crumb.id}>
              {' / '}
              <Link to={`/kategori/${crumb.slug}`} className="hover:text-ink-800">
                {crumb.name}
              </Link>
            </span>
          ))}
        </nav>
      ) : null}

      <ProductListPage
        key={slug}
        fixedFilter={{ categorySlug: slug, includeSubcategories: true }}
        title={category.data?.name}
        description={category.data?.description}
        subCategories={subCategories}
      />
    </>
  );
}

export function BrandPage() {
  const { slug = '' } = useParams();

  const brand = useQuery({
    queryKey: ['brand', slug],
    queryFn: () => catalogApi.brandBySlug(slug),
    enabled: slug.length > 0,
  });

  if (brand.isLoading) return <PageLoader />;

  return (
    <>
      <Seo
        title={brand.data?.seoTitle || (brand.data ? `${brand.data.name} Ürünleri ve Fiyatları` : null)}
        description={
          brand.data?.seoDescription ||
          brand.data?.description ||
          (brand.data ? `${brand.data.name} markasının en çok satan ürünleri ve özel kampanyaları.` : null)
        }
        keywords={brand.data?.seoKeywords}
        image={brand.data?.logoUrl || brand.data?.bannerUrl}
        breadcrumbs={[
          { name: 'Ana sayfa', url: '/' },
          { name: 'Markalar', url: '/markalar' },
          { name: brand.data?.name ?? slug, url: `/marka/${slug}` },
        ]}
      />
      <ProductListPage
        key={slug}
        fixedFilter={{ brandSlug: slug }}
        title={brand.data?.name}
        description={brand.data?.description}
      />
    </>
  );
}

export function CollectionPage() {
  const { slug = '' } = useParams();

  const collection = useQuery({
    queryKey: ['collection', slug],
    queryFn: () => catalogApi.collectionBySlug(slug),
    enabled: slug.length > 0,
  });

  if (collection.isLoading) return <PageLoader />;

  return (
    <>
      <Seo
        title={collection.data?.seoTitle || (collection.data ? `${collection.data.title} Fırsatları` : null)}
        description={
          collection.data?.seoDescription ||
          collection.data?.description ||
          (collection.data ? `${collection.data.title} koleksiyonundaki en özel seçilmiş indirimli ürünleri kaçırmayın.` : null)
        }
        keywords={collection.data?.seoKeywords}
        image={collection.data?.imageUrl || collection.data?.bannerUrl}
        breadcrumbs={[
          { name: 'Ana sayfa', url: '/' },
          { name: 'Koleksiyonlar', url: '/koleksiyonlar' },
          { name: collection.data?.title ?? slug, url: `/koleksiyon/${slug}` },
        ]}
      />
      <ProductListPage
        key={slug}
        fixedFilter={{ collectionSlug: slug }}
        title={collection.data?.title}
        description={collection.data?.description}
      />
    </>
  );
}

/* ------------------------------ 404 ------------------------------ */

export function NotFoundPage() {
  return (
    <div className="container-page py-24">
      <Seo title="Sayfa Bulunamadı" noIndex />
      <EmptyState
        title="Sayfa bulunamadı"
        description="Aradığınız sayfa taşınmış veya kaldırılmış olabilir."
        action={
          <Link to="/" className="btn-primary">
            Ana sayfaya dön
          </Link>
        }
      />
    </div>
  );
}

/* --------------------------- Marka listesi --------------------------- */

export function BrandListPage() {
  const [page, setPage] = useState(1);

  const brands = useQuery({
    queryKey: ['brands', page],
    queryFn: () => catalogApi.searchBrands({ page, pageSize: 24 }),
    placeholderData: keepPreviousData,
  });

  if (brands.isLoading) return <PageLoader />;

  const data = brands.data!;

  return (
    <div className="container-page py-10">
      <Seo
        title="Tüm Markalar"
        description="Mağazamızdaki tüm seçkin markaları, en popüler üreticileri ve orijinal ürünleri inceleyin."
      />
      <h1 className="mb-6 text-2xl font-semibold tracking-tight text-ink-900">Markalar</h1>

      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4 lg:grid-cols-6">
        {data.items.map((brand) => (
          <Link
            key={brand.id}
            to={`/marka/${brand.slug}`}
            className="flex flex-col items-center gap-2 rounded-xl border border-ink-200 p-5 text-center transition hover:shadow-card"
          >
            <div className="flex h-12 w-12 items-center justify-center overflow-hidden rounded-lg bg-ink-50">
              {brand.logoUrl ? (
                <img src={brand.logoUrl} alt="" className="h-full w-full object-contain" />
              ) : (
                <span className="text-lg font-semibold text-ink-400">{brand.name[0]}</span>
              )}
            </div>
            <span className="text-sm font-medium text-ink-800">{brand.name}</span>
            <span className="text-xs text-ink-400">{brand.productCount} ürün</span>
          </Link>
        ))}
      </div>

      <Pagination
        page={data.page}
        totalPages={data.totalPages}
        totalCount={data.totalCount}
        onChange={setPage}
      />
    </div>
  );
}

/* ------------------------ Koleksiyon listesi ------------------------ */

export function CollectionListPage() {
  const [page, setPage] = useState(1);

  const collections = useQuery({
    queryKey: ['collections', page],
    queryFn: () => catalogApi.searchCollections({ page, pageSize: 12 }),
    placeholderData: keepPreviousData,
  });

  if (collections.isLoading) return <PageLoader />;

  const data = collections.data!;

  return (
    <div className="container-page py-10">
      <Seo
        title="Özel Koleksiyonlar & Kampanyalar"
        description="Sezona özel koleksiyonlar, indirimli fırsatlar ve özenle seçilmiş ürün gruplarını keşfedin."
      />
      <h1 className="mb-6 text-2xl font-semibold tracking-tight text-ink-900">Koleksiyonlar</h1>

      <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {data.items.map((collection) => (
          <Link
            key={collection.id}
            to={`/koleksiyon/${collection.slug}`}
            className="relative flex aspect-[3/2] items-end overflow-hidden rounded-xl bg-ink-800"
          >
            {collection.imageUrl ? (
              <img
                src={collection.imageUrl}
                alt=""
                className="absolute inset-0 h-full w-full object-cover opacity-70"
              />
            ) : null}
            <div className="relative p-5">
              <p className="text-lg font-semibold text-white">{collection.title}</p>
              <p className="text-sm text-white/80">{collection.productCount} ürün</p>
            </div>
          </Link>
        ))}
      </div>

      <Pagination
        page={data.page}
        totalPages={data.totalPages}
        totalCount={data.totalCount}
        onChange={setPage}
      />
    </div>
  );
}
